"use client"

import { motion } from "framer-motion"
import { GraduationCap, Award, BookOpen, Users } from "lucide-react"

const CREDENTIALS = [
  { icon: GraduationCap, text: "M.A. Public Administration – Osmania University" },
  { icon: Award,         text: "Former Senior Faculty – TSPSC Study Circle, Hyderabad" },
  { icon: BookOpen,      text: "Author of 'Cracking Group 1' — Revised Edition, 2024" },
  { icon: Users,         text: "Mentored 2,000+ TSPSC & APPSC aspirants since 2011" },
]

export default function FounderSection() {
  return (
    <section className="bg-white border-t border-slate-100 py-12 sm:py-16 lg:py-20 overflow-hidden relative">

      {/* Ghost watermark */}
      <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none select-none opacity-[0.025]">
        <GraduationCap className="w-[320px] h-[320px] sm:w-[420px] sm:h-[420px] lg:w-[480px] lg:h-[480px]" strokeWidth={0.4} />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 xl:gap-20 items-center">

          {/* ── LEFT: Text content ── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="order-2 lg:order-1"
          >
            {/* Label */}
            <div className="flex items-center gap-4 mb-5 sm:mb-6">
              <div className="w-8 h-px bg-[#b48c36]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.4em] sm:tracking-[0.5em] text-[#b48c36]">
                A Word from the Founder
              </p>
            </div>

            {/* Name & Title */}
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-[2.75rem] text-slate-900 leading-tight mb-2 tracking-tight">
              Komma Rohith Sir
            </h2>
            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-6 sm:mb-8">
              Founder &amp; Chief Mentor · Rohith's Academy
            </p>

            {/* Bio quote */}
            <blockquote className="font-serif text-base sm:text-lg lg:text-xl text-slate-600  leading-relaxed border-l-2 border-[#b48c36] pl-4 sm:pl-5 mb-6 sm:mb-8">
              "Every student who walks into this academy carries a dream of serving the public.
              My mission is to ensure no sincere aspirant fails for lack of the right guidance."
            </blockquote>

            {/* Short bio */}
            <p className="text-sm text-slate-500 leading-relaxed mb-6 sm:mb-8 font-medium">
              Komma Rohith Sir is the guiding force behind Rohith's Academy. With over a
              decade of hands-on experience mentoring TSPSC and APPSC aspirants, he has
              built a pedagogy grounded in official syllabi, administrative case studies,
              and real exam psychology. His disciplined approach to General Studies and
              Answer Writing has produced over 45 final selections — including multiple
              Rank 1 holders across consecutive exam cycles.
            </p>

            {/* Credentials */}
            <ul className="space-y-3">
              {CREDENTIALS.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 border border-[#b48c36]/30 flex items-center justify-center text-[#b48c36] shrink-0 mt-0.5">
                    <Icon size={14} strokeWidth={1.5} />
                  </div>
                  <span className="text-xs sm:text-[13px] text-slate-500 font-medium leading-relaxed">{text}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* ── RIGHT: Photo ── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Decorative offset frame */}
              <div className="absolute -bottom-3 -right-3 w-full h-full border-2 border-[#b48c36]/20 z-0" />

              {/* Photo container */}
              <div className="relative w-[240px] sm:w-[300px] md:w-[340px] lg:w-[360px] aspect-[3/4] overflow-hidden bg-slate-100 z-10 shadow-2xl">
                <img
                  src="/founder.png"
                  alt="Komma Rohith Sir – Founder & Chief Mentor, Rohith's Academy"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
                {/* Name plate */}
                <div className="absolute bottom-0 left-0 right-0 bg-slate-950/90 backdrop-blur-sm px-4 sm:px-5 py-3 sm:py-4">
                  <p className="font-serif text-white text-sm sm:text-base font-bold leading-none mb-1">Komma Rohith Sir</p>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-[#b48c36]">Founder &amp; Chief Mentor</p>
                </div>
              </div>

              {/* Stat badge */}
              <div className="absolute -left-4 sm:-left-6 top-8 sm:top-10 bg-white border border-slate-100 shadow-xl px-3 sm:px-4 py-2.5 sm:py-3 z-20">
                <p className="font-serif font-black text-xl sm:text-2xl text-slate-900 leading-none">45+</p>
                <p className="text-[8px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">Final Selections</p>
              </div>

              {/* Years badge */}
              <div className="absolute -right-4 sm:-right-6 bottom-16 sm:bottom-20 bg-[#b48c36] px-3 sm:px-4 py-2.5 sm:py-3 z-20 shadow-xl">
                <p className="font-serif font-black text-xl sm:text-2xl text-white leading-none">13+</p>
                <p className="text-[8px] font-bold uppercase tracking-widest text-white/70 mt-0.5">Years Mentoring</p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}

